import { SiteTemplate } from "@/types/templates";

export const portfolioTemplate: SiteTemplate = {
  id: "portfolio-creative",
  name: "크리에이티브 포트폴리오",
  slug: "portfolio-creative",
  description: "디자이너, 개발자, 아티스트를 위한 현대적인 포트폴리오 템플릿",
  category: "PORTFOLIO",
  pricing: "FREE",

  sections: {
    hero: {
      type: "hero",
      name: "인트로 섹션",
      required: true,
      order: 1,
      defaultContent: {
        title: "안녕하세요, 저는 창작자입니다",
        subtitle: "디자인과 기술로 세상을 더 아름답게 만듭니다",
        description:
          "창의적이고 혁신적인 작업을 통해 고객의 비전을 현실로 만들어갑니다.",
        ctaText: "포트폴리오 보기",
        ctaUrl: "#portfolio",
        backgroundImage: "/images/templates/portfolio/hero-bg.jpg",
        overlay: true,
        overlayOpacity: 0.5,
      },
      settings: {
        height: "screen",
        alignment: "center",
        animation: "fadeIn",
      },
    },

    about: {
      type: "about",
      name: "소개",
      required: true,
      order: 2,
      defaultContent: {
        title: "저에 대해",
        subtitle: "열정과 전문성으로 무장한 창작자",
        description:
          "5년 이상의 경험을 바탕으로 브랜딩, 웹 디자인, UI/UX 디자인 분야에서 다양한 프로젝트를 성공적으로 완료했습니다. 고객의 스토리를 시각적으로 표현하는 것이 저의 전문 분야입니다.",
        image: "/images/templates/portfolio/about-image.jpg",
        features: [
          {
            icon: "palette",
            title: "브랜드 디자인",
            description: "독창적이고 기억에 남는 브랜드 아이덴티티 구축",
          },
          {
            icon: "monitor",
            title: "웹 디자인",
            description: "사용자 경험을 중시하는 반응형 웹사이트 디자인",
          },
          {
            icon: "smartphone",
            title: "UI/UX 디자인",
            description: "직관적이고 사용하기 쉬운 인터페이스 설계",
          },
        ],
        stats: [
          {
            value: "50+",
            label: "완료 프로젝트",
            description: "성공적으로 완료한 프로젝트 수",
          },
          {
            value: "5+",
            label: "경력 년수",
            description: "전문 디자이너로서의 경력",
          },
          {
            value: "100%",
            label: "고객 만족도",
            description: "모든 고객이 만족한 결과",
          },
        ],
      },
    },

    gallery: {
      type: "gallery",
      name: "포트폴리오",
      required: true,
      order: 3,
      defaultContent: {
        title: "포트폴리오",
        subtitle: "최근 작업들을 소개합니다",
        categories: ["전체", "브랜딩", "웹 디자인", "UI/UX"],
        projects: [
          {
            id: 1,
            title: "테크 스타트업 브랜딩",
            category: "브랜딩",
            description: "혁신적인 테크 스타트업을 위한 브랜드 아이덴티티 개발",
            image: "/images/templates/portfolio/project-1.jpg",
            technologies: ["Illustrator", "Photoshop", "Figma"],
            url: "/portfolio/tech-startup-branding",
            featured: true,
          },
          {
            id: 2,
            title: "이커머스 웹사이트",
            category: "웹 디자인",
            description: "현대적이고 사용자 친화적인 온라인 쇼핑몰 디자인",
            image: "/images/templates/portfolio/project-2.jpg",
            technologies: ["Figma", "React", "Tailwind CSS"],
            url: "/portfolio/ecommerce-website",
            featured: true,
          },
          {
            id: 3,
            title: "모바일 앱 UI 디자인",
            category: "UI/UX",
            description: "직관적인 사용자 경험을 제공하는 모바일 앱 인터페이스",
            image: "/images/templates/portfolio/project-3.jpg",
            technologies: ["Figma", "Principle", "InVision"],
            url: "/portfolio/mobile-app-ui",
            featured: false,
          },
        ],
      },
    },

    testimonials: {
      type: "testimonials",
      name: "고객 후기",
      required: false,
      order: 4,
      defaultContent: {
        title: "고객의 목소리",
        subtitle: "함께 작업한 고객들의 후기입니다",
        testimonials: [
          {
            name: "박지혜",
            position: "마케팅 디렉터",
            company: "이노베이션 컴퍼니",
            content:
              "정말 창의적이고 전문적인 작업이었습니다. 우리 브랜드의 가치를 완벽하게 표현해주었어요.",
            rating: 5,
            image: "/images/templates/portfolio/client-1.jpg",
          },
          {
            name: "김태호",
            position: "CEO",
            company: "테크솔루션",
            content:
              "기대 이상의 결과물이었습니다. 세심한 배려와 전문성이 돋보였어요.",
            rating: 5,
            image: "/images/templates/portfolio/client-2.jpg",
          },
        ],
      },
    },

    contact: {
      type: "contact",
      name: "연락하기",
      required: true,
      order: 5,
      defaultContent: {
        title: "프로젝트 문의",
        subtitle: "새로운 프로젝트를 함께 시작해보세요",
        email: "hello@portfolio.com",
        phone: "010-1234-5678",
        address: "서울특별시 강남구 논현로 123",
        hours: "평일 10:00 - 19:00",
        contactForm: {
          fields: [
            { name: "name", label: "이름", type: "text", required: true },
            { name: "email", label: "이메일", type: "email", required: true },
            { name: "company", label: "회사명", type: "text", required: false },
            {
              name: "project_type",
              label: "프로젝트 유형",
              type: "select",
              required: true,
              options: ["브랜딩", "웹 디자인", "UI/UX 디자인", "기타"],
            },
            {
              name: "budget",
              label: "예산 범위",
              type: "select",
              required: false,
              options: [
                "100만원 미만",
                "100-300만원",
                "300-500만원",
                "500만원 이상",
              ],
            },
            {
              name: "message",
              label: "프로젝트 설명",
              type: "textarea",
              required: true,
            },
          ],
          submitText: "프로젝트 문의하기",
          successMessage:
            "문의해주셔서 감사합니다. 24시간 내에 연락드리겠습니다.",
          errorMessage: "문의 전송 중 오류가 발생했습니다. 다시 시도해주세요.",
        },
      },
    },
  },

  styles: {
    colors: {
      primary: "#6366f1",
      secondary: "#8b5cf6",
      accent: "#f59e0b",
      background: "#ffffff",
      foreground: "#1f2937",
      muted: "#f9fafb",
      border: "#e5e7eb",
    },
    typography: {
      fontFamily: {
        sans: ["Poppins", "system-ui", "sans-serif"],
        heading: ["Playfair Display", "serif"],
      },
      fontSize: {
        xs: "0.75rem",
        sm: "0.875rem",
        base: "1rem",
        lg: "1.125rem",
        xl: "1.25rem",
        "2xl": "1.5rem",
        "3xl": "1.875rem",
        "4xl": "2.25rem",
        "5xl": "3rem",
      },
    },
    spacing: {
      section: "6rem",
      container: "1200px",
    },
    borderRadius: {
      sm: "0.375rem",
      md: "0.5rem",
      lg: "0.75rem",
      xl: "1rem",
    },
  },

  settings: {
    seo: {
      titleTemplate: "%s | Creative Portfolio",
      defaultTitle: "크리에이티브 포트폴리오",
      defaultDescription:
        "창의적이고 전문적인 디자인 작업을 통해 브랜드의 가치를 높입니다",
    },
    navigation: {
      position: "top",
      sticky: true,
      transparent: true,
      logo: {
        text: "Portfolio",
        image: "/images/templates/portfolio/logo.png",
      },
      menuItems: [
        { label: "홈", href: "#hero" },
        { label: "소개", href: "#about" },
        { label: "포트폴리오", href: "#gallery" },
        { label: "후기", href: "#testimonials" },
        { label: "연락처", href: "#contact" },
      ],
      ctaButton: {
        text: "프로젝트 문의",
        href: "#contact",
        style: "primary",
      },
    },
    footer: {
      showSocial: true,
      socialLinks: [
        { platform: "instagram", url: "" },
        { platform: "linkedin", url: "" },
        { platform: "twitter", url: "" },
      ],
      showNewsletter: false,
      copyright: "© 2024 Portfolio. All rights reserved.",
    },
    analytics: {
      googleAnalytics: "",
      facebookPixel: "",
    },
  },

  features: [
    "포트폴리오 갤러리",
    "프로젝트 상세 페이지",
    "연락 폼",
    "반응형 디자인",
    "SEO 최적화",
  ],
  tags: ["포트폴리오", "디자인", "창작", "아티스트", "프리랜서"],
  previewImage: "/images/templates/portfolio/preview.jpg",
  previewUrl: "/templates/portfolio/preview",

  isActive: true,
  isPublic: true,
  version: "1.0.0",
};
